import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import { useNavigate } from 'react-router';

import { EligibilityBlock } from 'api/hooks/private/users/students/useGetSelfScheduleEligibility';

import useAuthContext from 'hooks/useAuthContext';

interface EligibilityBlockAlertProps {
  block: EligibilityBlock;
  onActionClick?: () => void;
}

function EligibilityBlockAlert({ block, onActionClick }: EligibilityBlockAlertProps): JSX.Element {
  const navigate = useNavigate();
  const { userId: studentId } = useAuthContext();

  // Map block reason codes to alert severity
  const getSeverity = (): 'error' | 'warning' | 'info' => {
    const errorCodes = ['OUTSTANDING_BALANCE', 'PERMIT_EXPIRED', 'PERMIT_MISSING', 'PERMIT_EXPIRATION_MISSING'];

    const warningCodes = [
      'BALANCE_RESTRICTION',
      'PERMIT_EXPIRING',
      'MIN_INTERVAL_BETWEEN_DRIVES',
      'MAX_ADVANCE_BOOKINGS',
    ];

    if (errorCodes.includes(block.reason.code)) {
      return 'error';
    } else if (warningCodes.includes(block.reason.code)) {
      return 'warning';
    }
    return 'info';
  };

  // Handle action button click
  const handleActionClick = () => {
    if (onActionClick) {
      onActionClick();
      return;
    }

    // Special handling for UPDATE_PERMIT action - open dialog
    if (block.resolution.action === 'UPDATE_PERMIT' && studentId) {
      navigate('/student/personal-information');
      return;
    }

    // Default: navigate to URL if provided
    if (block.resolution.url) {
      navigate(block.resolution.url);
    }
  };

  // Render action button if applicable
  const renderActionButton = () => {
    if (block.resolution.action === 'NONE' || block.resolution.action === 'WAIT') {
      return undefined;
    }

    const buttonText = () => {
      switch (block.resolution.action) {
        case 'PAY':
          return 'View Ledger';
        case 'UPDATE_PERMIT':
          return 'Update Permit';
        case 'CANCEL_BOOKING':
          return 'View Conflict';
        case 'CONTACT_ADMIN':
          return 'Contact Support';
        default:
          return 'Resolve';
      }
    };

    return (
      <Button color="inherit" size="small" onClick={handleActionClick}>
        {buttonText()}
      </Button>
    );
  };

  return (
    <Alert variant="filled" severity={getSeverity()} action={renderActionButton()}>
      <strong>{block.reason.title}</strong>
      {block.reason.message && (
        <>
          <br />
          {block.reason.message}
        </>
      )}
      {block.resolution.message && (
        <>
          <br />
          <em>{block.resolution.message}</em>
        </>
      )}
    </Alert>
  );
}

export default EligibilityBlockAlert;
